import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AuthState {
  user: string | null;
  token: string | null;
  isAuthenticated: boolean;
  isValidating: boolean;
  login: (username: string, token: string) => void;
  logout: () => void;
  setValidating: (validating: boolean) => void;
  validateAndSetAuth: (isValid: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isValidating: true, // Start as validating until we verify token
      
      login: (username: string, token: string) => {
        set({
          user: username,
          token: token,
          isAuthenticated: true,
          isValidating: false,
        });
      },
      
      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isValidating: false,
        });
      },
      
      setValidating: (validating: boolean) => {
        set({ isValidating: validating });
      },
      
      validateAndSetAuth: (isValid: boolean) => {
        if (!isValid) {
          // Token is invalid, clear auth state
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isValidating: false,
          });
        } else {
          set({ isValidating: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      // Only persist these fields
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      // When rehydrating from storage, set isValidating based on token presence
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('Error rehydrating auth state:', error);
          return;
        }
        if (state) {
          if (state.token) {
            // We have a stored token, need to validate it
            state.isValidating = true;
          } else {
            // No token, no need to validate
            state.isValidating = false;
          }
        }
      },
    }
  )
);

// Export auth header helper
export const getAuthHeaders = (): Record<string, string> => {
  const { token } = useAuthStore.getState();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
};

// Helper to handle 401 responses - call this from API interceptor
export const handleUnauthorized = () => {
  const { logout } = useAuthStore.getState();
  logout();
};
